import React, { FC, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { ReactComponent as MessageIcon } from 'assets/images/message.svg';
import { Recipient } from 'components/Recipient';
import { Footer } from 'components/Footer';
import { Review } from 'components/Review';
import { Users } from 'components/Users';
import { useGlobalContext } from 'context/GlobalProvider';
import { useCountryContext } from 'context/CountryProvider';
import { Loader } from 'components/Loader';
import { useService } from 'common/hooks/useService';
import { useSearchParamsHook } from 'common/hooks/useSearchParamsHook';
import { useFunnelEntry } from 'common/hooks/useFunnelEntry';
import { EEntryType, EEventAction, EPaymentTab, EReviewTarget, ERoute, ESearchParams, EYandexMetrikaEvent } from 'common/const';
import { IUser } from 'common/types/user.types';
import { getEventPayload } from 'common/helpers/event.helper';
import { sendYandexMetrikaEvent } from 'common/helpers/yandex.helper';
import { getReviewParams } from 'common/helpers/metrika.helper';

export const WaiterListLayout: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getSearchParam, setSearchParam } = useSearchParamsHook();
  const { organization, setOrganization, setUser, setActiveTab } = useGlobalContext();
  const { applyOrganizationCountry } = useCountryContext();
  const { Service, loading } = useService();

  const visitorUid = getSearchParam(ESearchParams.VisitorUid);
  const organizationId = getSearchParam(ESearchParams.OrganizationId);
  const review = getSearchParam(ESearchParams.Review);
  const eventPayload = getEventPayload(visitorUid);
  // Источник адреса заведения переходит параметром source во все адреса, которые строятся заново;
  // метки utm_* в них не переходят (metrika-landing, R-2)
  const { source, sendLandingOpened } = useFunnelEntry();
  const sourceParam = `${ESearchParams.Source}=${source}`;
  const isReview = review === 'true';
  const reviewParams = getReviewParams(organization, source, EReviewTarget.Organization);
  const reviewShownRef = useRef<boolean>(false);

  const getOrganization = async (id: string) => {
    const response = await Service.getOrganizationById(id);
    const organization = response?.organization;

    if (organization) {
      // Страна применяется при любом ответе с организацией: и перед переходом к единственному официанту (US-034, R-3)
      applyOrganizationCountry(organization);

      const users = organization.users;
      const isSingleWaiter = users?.length === 1;

      // Вход с носителя: до перехода к единственному официанту и до показа списка, в том числе пустого (R-3)
      sendLandingOpened(organization, isSingleWaiter ? EEntryType.AutoEmployee : EEntryType.EmployeeList);

      if (!users || !users.length) {
        Sentry.captureMessage('The list of waiters is empty');
        return;
      }

      if (isSingleWaiter) {
        const user = users[0];

        navigate(`${ERoute.Payment}?${ESearchParams.UserId}=${user.uuid}&${sourceParam}`, { replace: true });
        return;
      }

      setOrganization(organization);
    }

    if (!visitorUid) {
      await Service.registerVisit();
    }
  };

  const onUserClick = (user: IUser) => {
    setUser(user);
    if (organization?.review_subscription) {
      navigate(
        `${ERoute.Payment}?${ESearchParams.UserId}=${user.uuid}&${ESearchParams.VisitorUid}=${visitorUid}&${ESearchParams.Review}=true&${sourceParam}`,
      );
    } else {
      navigate(`${ERoute.Payment}?${ESearchParams.UserId}=${user.uuid}&${ESearchParams.VisitorUid}=${visitorUid}&${sourceParam}`);
    }
  };

  const onReviewClick = () => {
    setSearchParam(ESearchParams.Review, true);
  };

  const onDislikeClick = () => {
    Service.sendEvent({ ...eventPayload, action: EEventAction.Dislike });
    sendYandexMetrikaEvent(EYandexMetrikaEvent.DislikeButtonClick, reviewParams);
    setActiveTab(EPaymentTab.Complain);
    navigate(
      `${ERoute.Payment}?${ESearchParams.OrganizationId}=${organizationId}&${ESearchParams.VisitorUid}=${visitorUid}&${ESearchParams.Review}=false&${sourceParam}`,
    );
  };

  const onLeaveTipsClick = () => {
    if (organization?.tips_disabled) {
      alert(t('payment.tips.askForHvalaTerminal'));
      return;
    }
    Service.sendEvent({ ...eventPayload, action: EEventAction.LikeAndTips });
    sendYandexMetrikaEvent(EYandexMetrikaEvent.LikeAndTipButtonClick, reviewParams);
    navigate(
      `${ERoute.Payment}?${ESearchParams.OrganizationId}=${organizationId}&${ESearchParams.VisitorUid}=${visitorUid}&${ESearchParams.Review}=false&${sourceParam}`,
    );
  };

  useEffect(() => {
    if (organizationId) {
      getOrganization(organizationId);
    }
  }, [organizationId]);

  // reviewOpened экрана заведения один раз на показ, после ответа о заведении; ответ с ошибкой - события нет (R-12)
  useEffect(() => {
    if (!isReview) {
      reviewShownRef.current = false;
      return;
    }

    if (!reviewShownRef.current && !loading && reviewParams) {
      reviewShownRef.current = true;
      sendYandexMetrikaEvent(EYandexMetrikaEvent.ReviewOpened, reviewParams);
    }
  }, [isReview, loading, organization]);

  if (loading) {
    return <Loader />;
  }

  if (review === 'true') {
    return (
      <div className="waiter-list-layout">
        <Recipient organizationId={organizationId} avatar={organization?.avatar} name={organization?.name} />

        <Review
          visitorUid={visitorUid}
          redirectUrl={organization?.review_url}
          onDislikeClick={onDislikeClick}
          onLeaveTipsClick={onLeaveTipsClick}
          metrikaParams={reviewParams}
        />

        <Footer />
      </div>
    );
  }

  return (
    <div className="waiter-list-layout">
      <Recipient organizationId={organizationId} avatar={organization?.avatar} name={organization?.name} />

      <div className="waiter-list-layout__container">
        <Users label={t('waiterList.usersListLabel')} users={organization?.users} onUserClick={onUserClick} />

        {organization?.review_subscription && (
          <div className="btn btn-submit">
            <button onClick={onReviewClick}>
              <MessageIcon />

              <span>{t('waiterList.review')}</span>
            </button>
          </div>
        )}
      </div>

      <Footer background="#f6f5fa" />
    </div>
  );
};
